import HeaderTransaction from 'components/HeaderTransaction'
import { Navigate, useLocation } from 'react-router-dom'
import { Container } from 'reactstrap'
import ItemInfo from './ItemInfo'
import NoteInfo from './NoteInfo'
import TotalAmountInfo from './TotalAmountInfo'
import UserInfo from './UserInfo'

import BreadCrumbs from 'components/breadcrumbs'
import { useAbilityAccess } from 'hooks/useAbilityAccess'
import moment from 'moment-timezone'
import ModalDrafts from 'private-views/transactions/ModalDrafts'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { formatInputNumber } from 'utility/Utils'
import { URL_406 } from 'utils/pathUrl'
import {
  BUSINESS_TRANSACTION_PURCHASE,
  CREATE,
  MAKE,
} from 'utils/subjectActions'
import { useFetchSettingsQuery } from '../settings/settingApiSlice'
import ModalInfo from './ModalInfo'
import { purchaseCommodityCheckUser } from './purchaseCommodityAction'
import {
  setInitialSeller,
  setItemCount,
  setOrders,
  setProject,
  toggleModalDraft,
} from './purchaseCommoditySlice'
import './style.scss'

const PurchaseCommodity = () => {
  const dispatch = useDispatch()
  const { isLoading, isSuccess, data } = useFetchSettingsQuery()

  const createPurchase = useAbilityAccess({
    action: CREATE,
    subject: BUSINESS_TRANSACTION_PURCHASE,
  })
  const makePurchase = useAbilityAccess({
    action: MAKE,
    subject: BUSINESS_TRANSACTION_PURCHASE,
  })

  const { orders, addItemCount, isOpenModalDraft } = useSelector(
    (state) => state.purchaseCommmodity,
  )
  const location = useLocation()
  const [fileNames, setFileNames] = useState([])

  // Store prefill data from "Ulangi Transaksi" to use when user clicks "Hapus"
  const prefillDataRef = useRef(null)
  // When arriving from "Ulangi Pesanan" and a draft exists, suppress
  // the autosave that writes to localStorage until user confirms/removes.
  const suppressAutoSaveRef = useRef(false)
  // Store draft data snapshot when modal is shown to prevent overwriting
  const draftDataRef = useRef(null)
  const { profile } = useSelector((state) => state.dashboard)

  const onConfirm = () => {
    // Use snapshot of draft data taken when modal appeared
    // This prevents data from being overwritten by auto-save effect
    const getUnifiedDraft = (userId) => {
      const key = `userData-${userId}`
      const raw = localStorage.getItem(key)
      if (raw && raw !== 'null') return JSON.parse(raw)
      return null
    }

    const raw = draftDataRef.current || getUnifiedDraft(profile?.userId)
    // Normalize legacy draft formats (dataComodities) into expected `orders` shape
    const buildOrdersFromLegacy = (d) => {
      if (!d) return null
      if (d.orders) return d.orders
      if (d.dataComodities && Array.isArray(d.dataComodities)) {
        const parseNumber = (v) => {
          if (v === null || v === undefined || v === '') return 0
          if (typeof v === 'number') return v
          const s = String(v).trim()
          const cleaned = s.replace(/\./g, '').replace(/,/g, '.')
          return Number(cleaned) || 0
        }

        const items = d.dataComodities.map((it, i) => {
          const numericPrice = parseNumber(it?.price ?? 0)
          const numericTotal = parseNumber(it?.totalPrice ?? 0)

          return {
            orderItemId: i + 1,
            SKU: it?.comodity?.sku || '',
            itemId: it?.comodity?.id ?? it?.comodity?.value ?? null,
            amount: it?.quantity ?? it?.amount ?? '',
            imageUrl: it?.comodity?.img || '',
            nameItem: it?.comodity?.label || it?.comodity?.name || '',
            price: numericPrice,
            totalPrice: numericTotal,
            unit: it?.unit || { label: 'Kg', value: 'Kg' },
          }
        })

        return {
          isLoading: false,
          step: 0,
          userId: profile?.userId || null,
          item: items,
          order: { type: null, notes: d.notes || '', transaction_number: '' },
          buyer: null,
          seller: d.dataSeller || null,
          trxDate: d.trxDate || moment(new Date()).format('YYYY-MM-DD'),
          project: null,
        }
      }
      return null
    }

    const data = raw
    const normalizeOrders = (orders) => {
      if (!orders) return orders
      const items = (orders.item || []).map((it) => {
        const unitStr = (it?.unit?.value ?? it?.unit ?? '')
          ?.toString()
          ?.toLowerCase()
        let rawAmount = it?.amount ?? 0
        let amount = rawAmount
        // parse numeric-like strings
        const parseNumber = (v) => {
          if (v === null || v === undefined || v === '') return 0
          if (typeof v === 'number') return v
          const s = String(v).trim()
          const cleaned = s.replace(/\./g, '').replace(/,/g, '.')
          return Number(cleaned) || 0
        }

        const numericRaw = parseNumber(rawAmount)
        // heuristic: when unit is kg and raw looks like scaled integer (e.g. 145 -> 1.45)
        amount = numericRaw

        const numericPrice = parseNumber(it?.price ?? 0)
        const totalPrice = amount * numericPrice

        return {
          ...it,
          amount,
          price: it?.price ?? it?.price,
          totalPrice,
        }
      })

      return {
        ...orders,
        item: items,
      }
    }

    const ordersToUse = data?.orders ?? buildOrdersFromLegacy(data)
    // If this draft came from legacy `dataComodities`, prefer the
    // `applyPrefillData` normalization (it preserves amounts correctly).
    if (data?.dataComodities) {
      const normalized = applyPrefillData({
        prefillOrders: ordersToUse,
        prefillAddItemCount: data?.addItemCount,
      })
      dispatch(setOrders(normalized))
      dispatch(setItemCount(data?.addItemCount || [1]))
    } else {
      dispatch(setOrders(normalizeOrders(ordersToUse)))
      // try to pick addItemCount from draft if available
      const addCount = data?.addItemCount || [1]
      dispatch(setItemCount(addCount))
    }
    const restoredFileNames = (data?.fileNames || []).map((file, idx) => ({
      ...file,
      url: file.url || ordersToUse?.order?.files?.[idx] || null,
    }))
    setFileNames(restoredFileNames)
    // user chose to use draft — re-enable autosave
    suppressAutoSaveRef.current = false
    // Clear refs - user chose to use draft
    prefillDataRef.current = null
    draftDataRef.current = null
    window.history.replaceState({}, document.title)
    dispatch(toggleModalDraft())
  }

  const onRemove = () => {
    // If user clicked Hapus while arriving from "Ulangi Pesanan", we should
    // *save* the prefill data to localStorage (so it becomes the draft), then
    // apply it. If not arriving from prefill, remove existing draft.
    if (prefillDataRef.current) {
      const normalized = applyPrefillData(prefillDataRef.current)
      // Apply prefill to in-memory state only (do NOT modify localStorage)
      dispatch(setOrders(normalized))
      dispatch(setItemCount(prefillDataRef.current.prefillAddItemCount || [1]))

      // Clear prefill data after use
      prefillDataRef.current = null
      window.history.replaceState({}, document.title)
      // re-enable autosave in case it was suppressed
      suppressAutoSaveRef.current = false
      dispatch(toggleModalDraft())
      return
    }

    // Not from prefill: treat as explicit delete of draft
    localStorage.removeItem(`userData-${profile?.userId}`)
    localStorage.removeItem(`userDataBuy-${profile?.userId}`)
    suppressAutoSaveRef.current = false
    dispatch(toggleModalDraft())
  }

  const applyPrefillData = (prefillData) => {
    if (!prefillData || !prefillData.prefillOrders) return

    const {
      prefillOrders,
      prefillAddItemCount,
      shouldFetchSeller,
      sellerPhoneNumber,
    } = prefillData

    const normalized = {
      ...prefillOrders,
      item: (prefillOrders.item || []).map((it) => {
        const unitStr = (it?.unit?.value ?? it?.unit ?? '')
          ?.toString()
          ?.toLowerCase()
        const parseNumber = (v) => {
          if (v === null || v === undefined || v === '') return 0
          if (typeof v === 'number') return v
          const s = String(v).trim()
          const cleaned = s.replace(/\./g, '').replace(/,/g, '.')
          return Number(cleaned) || 0
        }

        let numericAmount = parseNumber(it?.amount ?? 0)

        const numericPrice = parseNumber(it?.price ?? 0)

        return {
          ...it,
          amount: formatInputNumber(numericAmount),
          price: numericPrice,
          totalPrice: numericAmount * numericPrice,
        }
      }),
    }

    dispatch(setOrders(normalized))

    if (prefillOrders.project) {
      dispatch(setProject(prefillOrders.project))
    }

    if (prefillAddItemCount) {
      dispatch(setItemCount(prefillAddItemCount))
    }

    // Fetch seller if needed and populate orders.seller (including bank fields)
    if (shouldFetchSeller && sellerPhoneNumber) {
      dispatch(
        purchaseCommodityCheckUser({
          search: encodeURIComponent(sellerPhoneNumber),
          type: 'BUY',
        }),
      )
        .unwrap()
        .then((res) => {
          dispatch(setInitialSeller(res))
        })
        .catch(() => {})
    }

    return normalized
  }

  // Autosave removed: do NOT persist form state automatically to localStorage
  // to avoid overwriting an existing draft. Persistence happens only when the
  // user explicitly chooses to remove the draft (Hapus) from the draft modal.

  useEffect(() => {
    if (profile?.userId) {
      const getUnifiedDraft = (userId) => {
        const key = `userData-${userId}`
        const raw = localStorage.getItem(key)
        if (raw && raw !== 'null') return JSON.parse(raw)
        return null
      }

      const isDraftPresent = (d) => {
        if (!d) return false
        try {
          // legacy format: dataComodities
          if (
            d.dataComodities &&
            Array.isArray(d.dataComodities) &&
            d.dataComodities.length > 0
          )
            return true
          const orders = d.orders || {}
          const items = orders.item || []
          if (items.length > 0) {
            for (const it of items) {
              const amt = Number(it?.amount ?? it?.quantity ?? 0)
              if (!Number.isNaN(amt) && Number(amt) > 0) return true
            }
          }
          // fallback checks
          if (d.addItemCount && d.addItemCount.length > 0) return true
          if (d.invPacking && d.invPacking.length > 0) return true
        } catch (e) {
          return false
        }
        return false
      }

      const data = getUnifiedDraft(profile?.userId)
      const hasDraft = isDraftPresent(data)

      // Store prefill data from "Ulangi Pesanan" to use when user clicks "Hapus"
      if (location?.state?.prefillOrders) {
        prefillDataRef.current = {
          prefillOrders: location.state.prefillOrders,
          prefillAddItemCount: location.state.prefillAddItemCount,
          shouldFetchSeller: location.state.shouldFetchSeller,
          sellerPhoneNumber: location.state.sellerPhoneNumber,
        }
      }

      // Show draft modal if draft exists
      if (hasDraft) {
        // IMPORTANT: Snapshot draft data RIGHT NOW before anything can change it
        draftDataRef.current = data
        // suppress autosave to avoid overwriting the draft while modal is up
        dispatch(toggleModalDraft())
      } else if (location?.state?.prefillOrders) {
        // No draft but we have prefill data from "Ulangi Pesanan"
        // Apply prefill data directly
        try {
          applyPrefillData({
            prefillOrders: location.state.prefillOrders,
            prefillAddItemCount: location.state.prefillAddItemCount,
            shouldFetchSeller: location.state.shouldFetchSeller,
            sellerPhoneNumber: location.state.sellerPhoneNumber,
          })
          // Clear prefill data after use
          prefillDataRef.current = null
          window.history.replaceState({}, document.title)
        } catch (e) {
          console.warn('apply prefill failed', e)
        }
      }
    }
  }, [profile?.userId, location?.state?.prefillOrders, dispatch])

  if (makePurchase || createPurchase) {
    return (
      <div className="w-100">
        <ModalDrafts
          isOpen={isOpenModalDraft}
          toggle={() => dispatch(toggleModalDraft())}
          onConfirm={onConfirm}
          onRemove={onRemove}
        />
        <Container className="custom-container">
          <div className="d-flex align-items-start gap-2 pt-3">
            <BreadCrumbs breadCrumbActive={'Pembelian'} />
          </div>
          <HeaderTransaction />
          <div className="my-3"></div>
          <UserInfo />
          <div className="my-3"></div>
          <ItemInfo />
          <div className="my-3"></div>
          <NoteInfo fileNames={fileNames} setFileNames={setFileNames} />
          <div className="my-3 mb-5" style={{ height: 15 }}></div>
        </Container>
        <TotalAmountInfo
          isLoading={isLoading}
          isSuccess={isSuccess}
          data={data}
        />
        <ModalInfo />
      </div>
    )
  }

  return <Navigate to={URL_406} replace={true} />
}

export default PurchaseCommodity
